import React, {useState, useCallback} from 'react';
import {View, FlatList, StyleSheet, Alert, RefreshControl, Image, TouchableOpacity} from 'react-native';
import {Searchbar, Text, Snackbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useFocusEffect} from '@react-navigation/native';
import {useInventoryCatalogStore} from '../../store/useInventoryCatalogStore';
import AddStockModal from './AddStockModal';
import {colors, spacing} from '../../theme';

const LOW_STOCK_THRESHOLD = 5;

const getStockColor = (stock) => {
  if (stock <= 0) return colors.error;
  if (stock <= LOW_STOCK_THRESHOLD) return '#E65100';
  return colors.success;
};

const getStockBg = (stock) => {
  if (stock <= 0) return colors.error + '12';
  if (stock <= LOW_STOCK_THRESHOLD) return '#FFF3E0';
  return colors.success + '12';
};

const MaterialsTab = ({selectedOrgUnitId}) => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {materials, isLoading, fetchMaterials, deleteMaterial} = useInventoryCatalogStore();
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});
  const [stockModal, setStockModal] = useState({visible: false, item: null});

  useFocusEffect(
    useCallback(() => {
      fetchMaterials(search || undefined, selectedOrgUnitId);
    }, [fetchMaterials, search, selectedOrgUnitId]),
  );

  const handleSearch = useCallback((text) => {
    setSearch(text);
    fetchMaterials(text || undefined, selectedOrgUnitId);
  }, [fetchMaterials, selectedOrgUnitId]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchMaterials(search || undefined, selectedOrgUnitId);
    } finally {
      setRefreshing(false);
    }
  }, [fetchMaterials, search, selectedOrgUnitId]);

  const handleEdit = useCallback((item) => {
    navigation.navigate('AddEditMaterial', {item});
  }, [navigation]);

  const handleDelete = useCallback((item) => {
    Alert.alert(
      t('common.confirm'),
      t('inventory.deleteConfirm', {name: item.name}),
      [
        {text: t('common.cancel'), style: 'cancel'},
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteMaterial(item.id);
              setSnackbar({visible: true, message: 'Material deleted'});
            } catch (e) {
              setSnackbar({visible: true, message: e.response?.data?.message || t('common.error')});
            }
          },
        },
      ],
    );
  }, [t, deleteMaterial]);

  const handleAddStock = useCallback((item) => {
    setStockModal({visible: true, item});
  }, []);

  const renderItem = useCallback(({item}) => {
    const displayStock = selectedOrgUnitId != null && item.orgUnitStock != null
      ? item.orgUnitStock
      : item.currentStock;
    const stockColor = getStockColor(displayStock);
    const stockBg = getStockBg(displayStock);

    return (
      <View style={styles.card}>
        <View style={[styles.stockBar, {backgroundColor: stockColor}]} />
        <View style={styles.cardBody}>
          {item.mainImageUrl ? (
            <Image source={{uri: item.mainImageUrl}} style={styles.thumb} />
          ) : (
            <View style={styles.thumbPlaceholder}>
              <Icon name="package-variant" size={22} color={colors.textSecondary} />
            </View>
          )}
          <View style={styles.cardInfo}>
            <Text style={styles.itemName} numberOfLines={1}>{item.name}</Text>
            {item.materialTypeName && (
              <Text style={styles.itemType}>{item.materialTypeName}</Text>
            )}
            <View style={styles.metaRow}>
              {item.sku && (
                <Text style={styles.skuText}>{item.sku}</Text>
              )}
              {item.sellingPrice != null && (
                <Text style={styles.priceText}>{'\u20B9'}{item.sellingPrice}</Text>
              )}
            </View>
          </View>
          <View style={styles.stockSection}>
            <View style={[styles.stockBadge, {backgroundColor: stockBg}]}>
              <Text style={[styles.stockValue, {color: stockColor}]}>{displayStock}</Text>
              <Text style={[styles.stockUnit, {color: stockColor}]}>{item.materialUnitName || 'qty'}</Text>
            </View>
            <View style={styles.actionRow}>
              <TouchableOpacity onPress={() => handleAddStock(item)} style={styles.actionBtn}>
                <Icon name="plus-circle-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => handleEdit(item)} style={styles.actionBtn}>
                <Icon name="pencil-outline" size={20} color={colors.textSecondary} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => handleDelete(item)} style={styles.actionBtn}>
                <Icon name="delete-outline" size={20} color={colors.error} />
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    );
  }, [t, handleEdit, handleDelete, handleAddStock, selectedOrgUnitId]);

  const keyExtractor = useCallback((item) => String(item.id), []);

  return (
    <View style={styles.container}>
      <View style={styles.searchContainer}>
        <Searchbar
          placeholder={t('common.search')}
          value={search}
          onChangeText={handleSearch}
          style={styles.searchbar}
          inputStyle={styles.searchInput}
          iconColor={colors.textSecondary}
        />
      </View>
      <FlatList
        data={materials}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        contentContainerStyle={styles.list}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }
        ListEmptyComponent={
          !isLoading && (
            <View style={styles.emptyContainer}>
              <Icon name="package-variant-closed" size={48} color={colors.textLight} />
              <Text style={styles.emptyText}>{t('inventory.noMaterials')}</Text>
            </View>
          )
        }
      />
      <AddStockModal
        visible={stockModal.visible}
        item={stockModal.item}
        defaultOrgUnitId={selectedOrgUnitId}
        onDismiss={() => setStockModal({visible: false, item: null})}
        onSuccess={() => {
          setStockModal({visible: false, item: null});
          fetchMaterials(search || undefined, selectedOrgUnitId);
        }}
      />
      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1},
  searchContainer: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.divider,
  },
  searchbar: {backgroundColor: colors.background, elevation: 0},
  searchInput: {fontSize: 14},
  list: {paddingHorizontal: spacing.md, paddingTop: spacing.sm, paddingBottom: spacing.xl + 60},

  card: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    borderRadius: 10,
    marginBottom: spacing.sm,
    overflow: 'hidden',
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  stockBar: {width: 4},
  cardBody: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.sm,
    gap: spacing.sm,
  },
  thumb: {width: 48, height: 48, borderRadius: 8},
  thumbPlaceholder: {
    width: 48, height: 48, borderRadius: 8,
    backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center',
  },
  cardInfo: {flex: 1},
  itemName: {fontSize: 15, fontWeight: '600', color: colors.text},
  itemType: {fontSize: 12, color: colors.textSecondary, marginTop: 1},
  metaRow: {flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 3},
  skuText: {fontSize: 11, color: colors.textSecondary, backgroundColor: colors.background, paddingHorizontal: 5, paddingVertical: 1, borderRadius: 3},
  priceText: {fontSize: 13, fontWeight: '600', color: colors.primary},

  stockSection: {alignItems: 'center', gap: 4},
  stockBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    alignItems: 'center',
    minWidth: 50,
  },
  stockValue: {fontSize: 18, fontWeight: '800'},
  stockUnit: {fontSize: 10, fontWeight: '500'},
  actionRow: {flexDirection: 'row', gap: 2},
  actionBtn: {padding: 4},

  emptyContainer: {alignItems: 'center', paddingTop: 60},
  emptyText: {textAlign: 'center', marginTop: spacing.sm, color: colors.textSecondary},
});

export default MaterialsTab;
